/* Every task across the portfolio, plus the one that is not attached to
   anything.

   Grouped by owner or by milestone month, because those are the two questions
   people actually arrive with: "what am I on the hook for" and "what is due
   before the next review". Sorting a flat list by date answers neither
   without counting. */
import { useMemo, useState } from "react";

import { api, type Task } from "../api";
import { DueDate, today } from "../components/ui";
import { BetChips, InitiativeChip, NO_INITIATIVE } from "../components/strategy";
import { href } from "../route";
import { useStore } from "../store";

type Group = "owner" | "month" | "object" | "initiative" | "bet";

export default function Tasks() {
  const { tasks, people, meta, bets, initiatives, run } = useStore();
  const [group, setGroup] = useState<Group>("owner");
  const [status, setStatus] = useState("");
  const [owner, setOwner] = useState("");
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [title, setTitle] = useState("");
  const [initiative, setInitiative] = useState("");
  const [bet, setBet] = useState("");

  /* Which bets an initiative serves, for the bet filter and the chips. A name
     lookup over data the store already holds, never a recomputed count. */
  const betsOf = useMemo(() => {
    const map = new Map<number, number[]>();
    for (const i of initiatives) map.set(i.id, i.bet_ids);
    return map;
  }, [initiatives]);

  const filtered = useMemo(
    () =>
      tasks.filter((t) => {
        if (status && t.status !== status) return false;
        if (owner && (owner === "unassigned" ? !!t.owner_name : t.owner_name !== owner))
          return false;
        if (
          onlyOverdue &&
          !(t.next_milestone_date !== null &&
            t.next_milestone_date < today() &&
            t.status !== "done")
        ) {
          return false;
        }
        if (initiative) {
          if (initiative === NO_INITIATIVE) {
            if (t.initiative) return false;
          } else if (t.initiative?.id !== Number(initiative)) {
            return false;
          }
        }
        if (bet) {
          const ids = t.initiative ? (betsOf.get(t.initiative.id) ?? []) : [];
          if (bet === NO_INITIATIVE ? ids.length > 0 : !ids.includes(Number(bet))) return false;
        }
        return true;
      }),
    [tasks, status, owner, onlyOverdue, initiative, bet, betsOf],
  );

  const groups = useMemo(() => {
    const buckets = new Map<string, Task[]>();
    for (const task of filtered) {
      let keys: string[];
      if (group === "owner") keys = [task.owner_name ?? "Unassigned"];
      else if (group === "month")
        keys = [task.next_milestone_date ? task.next_milestone_date.slice(0, 7) : "No date"];
      else if (group === "initiative")
        keys = [
          task.initiative
            ? `${task.initiative.code} — ${task.initiative.name}`
            : "No initiative",
        ];
      else if (group === "bet") {
        /* A task under several bets is listed under each, the same way the
           overview repeats a crossover initiative. Walking a review bet by bet
           should not skip work because it serves two of them. */
        const ids = task.initiative ? (betsOf.get(task.initiative.id) ?? []) : [];
        const names = ids
          .map((id) => bets.find((b) => b.id === id))
          .filter(Boolean)
          .map((b) => `${b!.code} — ${b!.name}`);
        keys = names.length > 0 ? names : ["No bet"];
      } else
        keys = [task.attached_to?.name ?? "Standalone — not attached to anything"];
      for (const key of keys) buckets.set(key, [...(buckets.get(key) ?? []), task]);
    }
    return [...buckets.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered, group]);

  const add = () => {
    const text = title.trim();
    if (!text) return;
    setTitle("");
    void run(() => api.createTask({ title: text, status: "in progress" }));
  };

  return (
    <>
      <div className="filters">
        <label>
          Group by
          <select value={group} onChange={(e) => setGroup(e.target.value as Group)}>
            <option value="owner">Owner</option>
            <option value="month">Milestone month</option>
            <option value="object">Service or asset</option>
            <option value="initiative">Initiative</option>
            <option value="bet">Bet</option>
          </select>
        </label>
        <label>
          Initiative
          <select value={initiative} onChange={(e) => setInitiative(e.target.value)}>
            <option value="">Any</option>
            <option value={NO_INITIATIVE}>No initiative</option>
            {initiatives.map((i) => (
              <option key={i.id} value={i.id}>
                {i.code} — {i.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Bet
          <select value={bet} onChange={(e) => setBet(e.target.value)}>
            <option value="">Any</option>
            <option value={NO_INITIATIVE}>No bet</option>
            {bets.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} — {b.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Any</option>
            {(meta?.task_statuses ?? []).map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </label>
        <label>
          Owner
          <select value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">Any</option>
            <option value="unassigned">Unassigned</option>
            {people.map((p) => (
              <option key={p.person_id}>{p.display_name}</option>
            ))}
          </select>
        </label>
        <label style={{ flexDirection: "row", alignItems: "center", gap: 6, paddingBottom: 5 }}>
          <input
            type="checkbox"
            checked={onlyOverdue}
            onChange={(e) => setOnlyOverdue(e.target.checked)}
          />
          Overdue only
        </label>
        <span className="count">{filtered.length} tasks</span>
      </div>

      <div className="filters">
        <label className="grow">
          New task
          <input
            type="text"
            value={title}
            placeholder="title…"
            style={{ width: "100%" }}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
          />
        </label>
        <button className="btn" onClick={add} disabled={!title.trim()}>
          Add
        </button>
      </div>

      {groups.map(([name, items]) => (
        <section className="panel" key={name}>
          <div className="panel-hd">
            <h2>{name}</h2>
            <span className="muted">{items.length}</span>
          </div>
          {items.map((task) => (
            <article className="tcard" key={`${name}-${task.id}`}>
              <h4>
                <a href={href.task(task.id)}>{task.title}</a>
              </h4>
              <div className="meta">
                <span>{task.status}</span>
                <DueDate date={task.next_milestone_date} status={task.status} />
                <span>{task.owner_name ?? "Unassigned"}</span>
                {task.subtasks > 0 && (
                  <span>
                    checklist {task.subtasks_done}/{task.subtasks}
                  </span>
                )}
                {task.attached_to && (
                  <span>
                    <a href={href.object(task.attached_to.kind, task.attached_to.id)}>
                      {task.attached_to.name}
                    </a>
                  </span>
                )}
                {task.initiative && (
                  <span>
                    <InitiativeChip
                      initiative={task.initiative}
                      source={task.initiative.source}
                    />
                    <BetChips betIds={betsOf.get(task.initiative.id) ?? []} />
                  </span>
                )}
              </div>
            </article>
          ))}
        </section>
      ))}
      {groups.length === 0 && <p className="note">No tasks match these filters.</p>}
    </>
  );
}
